/**
 * This service is a bridge between the application and whatever analytics
 * software is used.
 *
 * By defining the REACT_APP_ANALYTICS_URL environment variable we are able to
 * either export the real or mocked implementation, so that from the application
 * point of view nothing changes and it can continue calling the analytics logic
 * as per usuall.
 */
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import merge from 'lodash/merge';
import get from 'lodash/get';
import { accordionClick, brandlinksClick } from './helper';

const mock = {
  default: {},
  datalayer: {},
  commons: {},
  pageData: {},
  pageBottom: function pageBottom() {},

  setDefaults: function setDefaults(obj) {
    Object.assign(this.default, obj);
  },

  addToDatalayer(additions) {
    Object.keys(additions).forEach(key => {
      set(this.datalayer, key, additions[key]);
    });
  },

  setDefaultsJourney: function setDefaults(commons) {
    this.commons = merge({}, this.commons, commons);
  },

  setCurrentPageDefaults: function setCurrentPageDefaults(pageData) {
    this.pageData = pageData;
  },

  setDealer: function setDealer(dealerData) {
    const dealer = {
      dealerData: {
        companyId: dealerData.companyId,
        companyName: dealerData.companyName,
        regionId: dealerData.regionId,
        address: {
          street: dealerData.addresses[0].street,
          zipCode: dealerData.addresses[0].zipCode,
          city: dealerData.addresses[0].city,
          state: null
        }
      }
    };
    this.default = merge({}, this.default, dealer);
  },

  setTrackingData: function setTrackingData(additions) {
    const clonedDefaultDatalayer = cloneDeep(this.default);
    if (window.du_digitalData && window.du_digitalData.core.pageInfo.pageName) {
      clonedDefaultDatalayer.core.pageInfo.pageName = window.du_digitalData.core.pageInfo.pageName;
    }
    const trackingDataCommons = {};
    let trackingDataPage = {};
    if (this.commons) {
      Object.keys(this.commons).forEach(key => {
        set(trackingDataCommons, key, this.commons[key]);
      });
    }
    if (this.pageData) {
      Object.keys(this.pageData).forEach(key => {
        set(trackingDataPage, key, this.pageData[key]);
      });
    }

    const trackingData = {};
    if (additions) {
      trackingDataPage = {};
      Object.keys(additions).forEach(key => {
        set(trackingData, key, additions[key]);
      });
      // if (trackingData.event) {
      //   for (let i = 0; i < trackingData.event.length; i++) {
      //     trackingDataPage.event.push(trackingData.event[i])
      //   }
      // }
    }
    window.du_digitalData = merge(
      {},
      clonedDefaultDatalayer,
      this.datalayer,
      trackingDataCommons,
      trackingDataPage,
      trackingData
    );
  },

  trackAccordion: function trackAccordion(linkInformation, faq) {
    this.setTrackingData({
      [this.keys.linkInformation(0)]: linkInformation,
      [this.keys.faq()]: faq,
      [this.keys.eventType(0)]: 'interAction',
      [this.keys.eventAction(0)]: 'Success'
    });
    this.trackInteraction();
  },

  trackContinueButton: function trackContinueButton(linkInformation, ev0, ev1, ev2, ev3) {
    const data = {
      [this.keys.linkInformation(0)]: linkInformation,
      [this.keys.eventType(0)]: ev0.type,
      [this.keys.eventAction(0)]: ev0.action
    };
    if (ev1) {
      data[this.keys.eventType(1)] = ev1.type;
      data[this.keys.eventAction(1)] = ev1.action;
    }
    if (ev2) {
      data[this.keys.eventType(2)] = ev2.type;
      data[this.keys.eventAction(2)] = ev2.action;
    }
    if (ev3) {
      data[this.keys.eventType(3)] = ev3.type;
      data[this.keys.eventAction(3)] = ev3.action;
    }
    this.setTrackingData(data);
    this.trackInteraction();
  },
  // Requested by Adobe team to not use the Start typing calls as of 17.02.2023
  // trackFirstInteraction: function trackFirstInteraction() {
  //   this.setTrackingData({
  //     [this.keys.linkInformation(0)]: 'Start typing',
  //     [this.keys.eventType(0)]: 'Form',
  //     [this.keys.eventAction(0)]: 'Start typing',
  //   })
  //   this.trackInteraction()
  // },
  // Requested by Adobe team to not use the Start typing calls as of 17.02.2023
  // trackZipFormFieldInteraction: function trackZipFormFieldInteraction() {
  //   this.setTrackingData({
  //     [this.keys.linkInformation(0)]: 'Start typing',
  //     [this.keys.eventType(0)]: 'Form',
  //     [this.keys.eventAction(0)]: 'Start typing',
  //   })
  //   this.trackInteraction()
  // },

  trackButton: function trackButton(linkInformation) {
    this.setTrackingData({
      [this.keys.linkInformation(0)]: linkInformation,
      [this.keys.eventType(0)]: 'interAction',
      [this.keys.eventAction(0)]: 'Success'
    });
    this.trackInteraction();
  },
  downloadDocument: function downloadDocument(linkInformation) {
    this.setTrackingData({
      [this.keys.linkInformation(0)]: linkInformation,
      [this.keys.eventType(0)]: 'Download',
      [this.keys.eventAction(0)]: 'Request'
    });
    this.trackInteraction();
  },
  trackError: function trackError(formFields) {
    this.setTrackingData({
      [this.keys.formErrors()]: Object.keys(formFields).join(', '),
      [this.keys.linkInformation(0)]: 'Field error',
      [this.keys.eventType(0)]: 'Form',
      [this.keys.eventAction(0)]: 'Field error'
    });
    this.trackInteraction();
  },

  trackApiError: function trackApiError() {
    this.setTrackingData({
      [this.keys.pageName()]: 'API error',
      [this.keys.viewChange()]: 'API error',
      [this.keys.eventType(0)]: 'pageView',
      [this.keys.eventAction(0)]: 'Success',
      [this.keys.eventType(1)]: 'API',
      [this.keys.eventAction(1)]: 'Error'
    });
    this.trackPage();
  },

  trackAbort: function trackAbort(fields) {
    this.setTrackingData({
      [this.keys.formErrors()]: fields,
      [this.keys.linkInformation(0)]: 'Form abort',
      [this.keys.eventType(0)]: 'Form',
      [this.keys.eventAction(0)]: 'Aborted'
    });
    this.trackInteraction();
  },

  trackPage: function page() {
    this.track('page');
  },

  trackInteraction: function interaction() {
    this.track('interaction');
  },

  track: function track(action) {
    if (typeof action !== 'string') {
      // eslint-disable-next-line no-console
      console.warn('Legacy tracking code detected');
      return;
    }
    // eslint-disable-next-line no-underscore-dangle
    if (!implementation.isLive || window.__CJ_DEBUG_ANALYTICS) {
      // eslint-disable-next-line no-console
      console.log(
        `track ${action}\n pageName: ${get(
          window.du_digitalData,
          this.keys.pageName()
        )}\n viewChange: ${get(
          window.du_digitalData,
          this.keys.viewChange()
        )}\n event0.info.linkInformation: ${get(
          window.du_digitalData,
          this.keys.linkInformation(0)
        )}\n event0.type: ${get(
          window.du_digitalData,
          this.keys.eventType(0)
        )}\n event0.action: ${get(window.du_digitalData, this.keys.eventAction(0))}\n\n Datalayer:`,
        window.du_digitalData
      );
    }
    // eslint-disable-next-line
    window._satellite && window._satellite.track(action);
  },

  trackLandingPage: function trackLandingPage(videoName) {
    this.setCurrentPageDefaults({
      [this.keys.pageName()]: 'home',
      [this.keys.viewChange()]: 'home',
      [this.keys.eventType(0)]: 'pageView',
      [this.keys.eventAction(0)]: 'Success',
      [this.keys.eventType(1)]: 'Product journey',
      [this.keys.eventAction(1)]: 'Start',
      [this.keys.video()]: videoName
    });
    this.setTrackingData();
    this.trackPage();
    const accordions = document.querySelectorAll('.c-accordion__header.js-accordion__title');
    accordions.forEach(item => {
      item.addEventListener('click', accordionClick);
    });
    const brandLinks = document.querySelectorAll('.o-content-section:last-child a');
    brandLinks.forEach(item => {
      item.addEventListener('click', brandlinksClick);
    });
  },

  trackProductPage: function trackProductPage() {
    this.setCurrentPageDefaults({
      [this.keys.pageName()]: 'Product Page',
      [this.keys.viewChange()]: 'Product Page',
      [this.keys.eventType(0)]: 'pageView',
      [this.keys.eventAction(0)]: 'Success'
    });
    this.setTrackingData();
    this.trackPage();
    const accordions = document.querySelectorAll('.c-accordion__header.js-accordion__title');
    accordions.forEach(item => {
      item.addEventListener('click', accordionClick);
    });
  },

  removeTrackingLanding: function removeTrackingLanding() {
    const accordions = document.querySelectorAll('.c-accordion__header.js-accordion__title');
    accordions.forEach(item => {
      item.removeEventListener('click', accordionClick);
    });
    const brandLinks = document.querySelectorAll('.o-content-section:last-child a');
    brandLinks.forEach(item => {
      item.removeEventListener('click', brandlinksClick);
    });
  },

  removeTrackingProduct: function removeTrackingProduct() {
    const accordions = document.querySelectorAll('.c-accordion__header.js-accordion__title');
    accordions.forEach(item => {
      item.removeEventListener('click', accordionClick);
    });
  },

  keys: {
    stagingEnvironment: () => 'core.stagingEnvironment',
    brand: () => 'core.attributes.brand',
    pageName: () => 'core.pageInfo.pageName',
    viewChange: () => 'core.attributes.viewChange',
    productVariants: () => 'core.category.productVariants',
    transactionId: () => 'core.attributes.transactionID',
    context: () => 'core.attributes.context',
    productVariant: () => 'product[0].productVariants',
    linkInformation: eventCount => `event[${eventCount}].eventInfo.linkInformation`,
    eventTargetURL: () => 'event[0].eventInfo.eventTargetURL',
    eventType: eventCount => `event[${eventCount}].eventInfo.eventType`,
    eventAction: eventCount => `event[${eventCount}].eventInfo.eventAction`,
    errorCode: () => 'error.errorCode',
    errorMessage: () => 'error.errorMessage',
    errorCausingURL: () => 'error.errorCausingURL',
    faq: () => 'core.attributes.faqText',
    formType: () => 'form.type',
    formName: () => 'form.name',
    formErrors: () => 'form.errorFields',
    subname: () => 'product[0].subname',
    condition: () => 'product[0].vehicleModel[0].condition',
    manufacturer: () => 'product[0].vehicleModel[0].manufacturer',
    model: () => 'product[0].vehicleModel[0].name',
    variation: () => 'product[0].vehicleModel[0].modelVariation',
    mileage: () => 'product[0].attributes.yearlyMileage',
    currentMileage: () => 'product[0].vehicleModel[0].currentMileage',
    duration: () => 'product[0].attributes.duration',
    payment: () => 'product[0].attributes.recurringPayment',
    allowPhoneAds: () => 'dataPrivacyStatement.allowPhoneAds',
    allowElectronicAds: () => 'dataPrivacyStatement.allowElectronicAds',
    allowDataTransfer: () => 'dataPrivacyStatement.allowDataTransfer',
    allowMailCommunication: () => 'dataPrivacyStatement.allowMailCommunication',
    initialRegistrationDate: () => 'product[0].vehicleModel[0].initialRegistrationDate',
    customerYearOfBirth: () => 'customerData.yearOfBirth',
    gender: () => 'customerData.gender',
    video: () => 'video.videoName',
    traci_s: () => 'traci._s',
    traci_ee: () => 'traci._ee',
    traci_ie: () => 'traci._ie',
    traci_a: () => 'traci._a',
    traci_p: () => 'traci._p',
    traci_c: () => 'traci._c',
    traci_t: () => 'traci._t'
  }
};

const real = {
  ...mock,
  isLive: true,
  pageBottom: function pageBottom() {
    window._satellite && window._satellite.pageBottom(); // eslint-disable-line
  }
};

let implementation = mock; // eslint-disable-line

if (process.env.REACT_APP_ANALYTICS_URL) {
  implementation = real;
}

export { implementation as Analytics };
